import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { Mail, Instagram } from 'lucide-react';

// Pinterest icon (Lucide doesn't ship one) — official "P" mark as inline SVG
const PinterestIcon = ({ className }) => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    fill="currentColor"
    aria-hidden="true"
  >
    <path d="M12.017 0C5.396 0 .029 5.367.029 11.987c0 5.079 3.158 9.417 7.618 11.162-.105-.949-.199-2.403.041-3.439.219-.937 1.406-5.957 1.406-5.957s-.359-.72-.359-1.781c0-1.663.967-2.911 2.168-2.911 1.024 0 1.518.769 1.518 1.688 0 1.029-.653 2.567-.992 3.992-.285 1.193.6 2.165 1.775 2.165 2.128 0 3.768-2.245 3.768-5.487 0-2.861-2.063-4.869-5.008-4.869-3.41 0-5.409 2.562-5.409 5.199 0 1.033.394 2.143.889 2.741.099.12.112.225.085.345-.09.375-.293 1.199-.334 1.363-.053.225-.172.271-.402.165-1.495-.69-2.433-2.878-2.433-4.646 0-3.776 2.748-7.252 7.92-7.252 4.158 0 7.392 2.967 7.392 6.923 0 4.135-2.607 7.462-6.233 7.462-1.214 0-2.357-.629-2.748-1.378l-.748 2.853c-.271 1.043-1.002 2.35-1.492 3.146C9.57 23.812 10.763 24 12.017 24c6.624 0 11.99-5.367 11.99-11.987C24.007 5.367 18.641.001 12.017.001z" />
  </svg>
);

const Footer = () => {
  const { t } = useLanguage();

  return (
    <footer className="bg-gray-900 text-white mt-20" data-testid="main-footer">
      {/* Affiliate disclosure strip — generic, network-independent, legally clear */}
      <div className="bg-gray-950 border-b border-gray-800" data-testid="affiliate-disclosure">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-3.5 text-center text-xs text-gray-400 leading-relaxed">
          {t(
            'U&Fashion is een onafhankelijk mode-inspiratieplatform. We tonen trends en collecties van diverse geselecteerde partner-webshops. Wanneer je via onze site doorklikt naar een webshop, kunnen wij hiervoor een kleine commissie ontvangen — dit heeft geen enkele invloed op de prijs die jij betaalt. Wij verkopen zelf geen producten.',
            'U&Fashion is an independent fashion-inspiration platform. We showcase trends and collections from selected partner webshops. When you click through to a webshop via our site, we may receive a small commission — this has no impact on the price you pay. We do not sell any products ourselves.',
            'U&Fashion est une plateforme indépendante d\u2019inspiration mode. Nous présentons les tendances et collections de plusieurs boutiques partenaires sélectionnées. Lorsque vous cliquez vers une boutique via notre site, nous pouvons recevoir une petite commission — cela n\u2019affecte en rien le prix que vous payez. Nous ne vendons aucun produit nous-mêmes.'
          )}
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          {/* Brand */}
          <div className="col-span-1 md:col-span-2">
            <div className="flex items-center space-x-2 mb-4">
              <img
                src="/uandfashion-logo.png"
                alt="U&Fashion logo"
                width="36"
                height="36"
                className="h-9 w-9 rounded-md shadow-sm"
              />
              <h3 className="text-2xl font-bold bg-gradient-to-r from-pink-400 via-purple-400 to-indigo-400 bg-clip-text text-transparent">
                U&Fashion
              </h3>
            </div>
            <p className="text-gray-400 mb-4">
              {t(
                'Een onafhankelijk fashion platform met een zorgvuldig samengestelde selectie van merken, inspiratie en stijl voor elke gelegenheid.',
                'An independent fashion platform with a carefully curated selection of brands, inspiration and style for every occasion.',
                'Une plateforme mode indépendante avec une sélection soigneusement curée de marques, d\u2019inspiration et de style pour chaque occasion.'
              )}
            </p>
            <div className="flex space-x-4">
              <a
                href="https://nl.pinterest.com/debeliemarc/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-400 hover:text-pink-400 transition-colors"
                aria-label="Pinterest"
                data-testid="footer-pinterest-icon"
              >
                <PinterestIcon className="w-5 h-5" />
              </a>
              <a
                href="https://instagram.com/uandfashion.eu"
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-400 hover:text-pink-400 transition-colors"
                aria-label="Instagram"
                data-testid="footer-instagram-icon"
              >
                <Instagram className="w-5 h-5" />
              </a>
              <Link to="/contact" className="text-gray-400 hover:text-pink-400 transition-colors" aria-label="Contact" data-testid="footer-contact-icon">
                <Mail className="w-5 h-5" />
              </Link>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-lg font-semibold mb-4">{t('Snelle Links', 'Quick Links', 'Liens rapides')}</h4>
            <ul className="space-y-2">
              <li>
                <Link to="/" className="text-gray-400 hover:text-pink-400 transition-colors">
                  {t('Home', 'Home', 'Accueil')}
                </Link>
              </li>
              <li>
                <Link to="/about" className="text-gray-400 hover:text-pink-400 transition-colors">
                  {t('Over Ons', 'About Us', 'À propos')}
                </Link>
              </li>
              <li>
                <Link
                  to="/acties"
                  className="text-gray-400 hover:text-pink-400 transition-colors"
                  data-testid="footer-action-alerts-link"
                >
                  {t('Acties', 'Action Alerts', 'Promotions')}
                </Link>
              </li>
              <li>
                <Link
                  to="/for-brands"
                  className="text-gray-400 hover:text-pink-400 transition-colors"
                  data-testid="footer-for-brands-link"
                >
                  {t('Voor Merken', 'For Brands', 'Pour les marques')}
                </Link>
              </li>
              <li>
                <Link
                  to="/contact"
                  className="text-gray-400 hover:text-pink-400 transition-colors"
                  data-testid="footer-contact-link"
                >
                  {t('Contact', 'Contact', 'Contact')}
                </Link>
              </li>
              <li>
                <a href="/sitemap.xml" className="text-gray-400 hover:text-pink-400 transition-colors" target="_blank" rel="noopener">
                  Sitemap
                </a>
              </li>
            </ul>
          </div>

          {/* Legal */}
          <div>
            <h4 className="text-lg font-semibold mb-4">{t('Juridisch', 'Legal', 'Mentions légales')}</h4>
            <ul className="space-y-2">
              <li>
                <Link to="/privacy" className="text-gray-400 hover:text-pink-400 transition-colors" data-testid="footer-privacy-link">
                  {t('Privacybeleid', 'Privacy Policy', 'Politique de confidentialité')}
                </Link>
              </li>
              <li>
                <Link to="/terms" className="text-gray-400 hover:text-pink-400 transition-colors">
                  {t('Algemene Voorwaarden', 'Terms of Service', 'Conditions générales')}
                </Link>
              </li>
              <li>
                <Link to="/disclaimer" className="text-gray-400 hover:text-pink-400 transition-colors">
                  {t('Affiliate Disclaimer', 'Affiliate Disclaimer', 'Avertissement affiliation')}
                </Link>
              </li>
            </ul>
          </div>
        </div>

        <div className="border-t border-gray-800 mt-8 pt-8 text-center text-gray-400">
          <p>© 2025 U&Fashion.eu - {t('Alle rechten voorbehouden', 'All rights reserved', 'Tous droits réservés')}</p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
